import { Controller } from "@hotwired/stimulus";
import { announce, fillTemplate } from "../utils/announce";
import { AttributeLease } from "../utils/attribute_lease";
import { DetachGate } from "../utils/detach_gate";
import { MinDurationFloor } from "../utils/min_duration_floor";
import { NUMBER_BOUNDS, type NumberValueConstraints } from "../utils/number_bounds";
import { NumberValueReader } from "../utils/number_value";
import { SafeTimeout } from "../utils/safe_timeout";

/**
 * Headless skeleton/placeholder manager. No dedicated APG pattern; it follows
 * the `aria-busy` + show/hide practice.
 *
 * Markup contract (identifier: `stimeo--skeleton`):
 *   <div data-controller="stimeo--skeleton" aria-busy="true"
 *        data-stimeo--skeleton-min-duration-value="300"
 *        data-action="content:ready->stimeo--skeleton#ready">
 *     <div aria-hidden="true" data-stimeo--skeleton-target="placeholder"></div>
 *     <div hidden data-stimeo--skeleton-target="content"></div>
 *   </div>
 *
 * Starts in the loading state (placeholder shown, real content hidden, region
 * `aria-busy="true"`); `ready` swaps to the content and clears busy. The
 * placeholder is `aria-hidden` (decorative) so assistive tech never reads the
 * skeleton. `minDuration` keeps the placeholder up long enough to avoid a flash
 * when content arrives almost immediately.
 *
 * `ready` dispatches `{}`.
 *
 * @remarks
 * Behavior only — skeleton shapes/animation are the consumer's. The
 * min-duration wait is held by `MinDurationFloor` on a `SafeTimeout`,
 * kept across an in-page move and dropped on a real detach via `DetachGate`.
 */
export class SkeletonController extends Controller<HTMLElement> {
  /** Numeric read boundaries share one reader for this controller instance. */
  readonly #numbers = new NumberValueReader();

  static override targets = ["placeholder", "content"];
  static override values = {
    announceReadyText: { type: String, default: "" },
    minDuration: { type: Number, default: 0 },
  };

  static valueConstraints = {
    minDuration: NUMBER_BOUNDS.timer,
  } satisfies NumberValueConstraints<typeof SkeletonController.values>;
  static actions = ["ready", "reset"] as const;
  static events = ["ready"] as const;

  declare readonly placeholderTarget: HTMLElement;
  declare readonly placeholderTargets: HTMLElement[];
  declare readonly contentTarget: HTMLElement;
  declare readonly contentTargets: HTMLElement[];
  declare readonly hasPlaceholderTarget: boolean;
  declare readonly hasContentTarget: boolean;

  declare minDurationValue: number;
  declare announceReadyTextValue: string;

  readonly #timers = new SafeTimeout();
  readonly #floor = new MinDurationFloor(this.#timers);
  readonly #gate = new DetachGate();
  /** Owns the `hidden` written on each placeholder and content, so a departed one gets it back. */
  readonly #hidden = new AttributeLease<HTMLElement>("hidden", this.identifier);
  /** Whether `connect()` has run and `disconnect()` has not since. */
  #connected = false;

  override connect(): void {
    this.#connected = true;
    // A probe still queued means this is the reconnect half of an in-page move.
    // The placeholder never left the screen, so the loading hooks already hold
    // the values `#enterLoading()` would write and the floor is still measuring
    // from the moment the skeleton actually appeared — restarting it here would
    // hold the reveal back for longer than `minDuration` asks.
    const moved = this.#gate.pending;
    this.#gate.cancel();
    if (!moved && this.#state !== "ready") {
      this.#enterLoading();
    }
  }

  override disconnect(): void {
    this.#connected = false;
    this.#gate.disconnected(this, () => this.#teardown());
  }

  /** Shows or hides a placeholder that arrives at runtime as the phase calls for. */
  placeholderTargetConnected(): void {
    if (this.#connected) this.#reflect(this.#state === "ready");
  }

  /**
   * Gives a placeholder that no longer resolves its own `hidden` back, even after
   * `disconnect()`, and while connected repaints the placeholder that stays.
   */
  placeholderTargetDisconnected(placeholder: HTMLElement): void {
    if (!this.placeholderTargets.includes(placeholder)) this.#hidden.return(placeholder);
    if (this.#connected) this.#reflect(this.#state === "ready");
  }

  /** Shows or hides content that arrives at runtime as the phase calls for. */
  contentTargetConnected(): void {
    if (this.#connected) this.#reflect(this.#state === "ready");
  }

  /**
   * Gives content that no longer resolves its own `hidden` back, even after
   * `disconnect()`, and while connected repaints the content that stays.
   */
  contentTargetDisconnected(content: HTMLElement): void {
    if (!this.contentTargets.includes(content)) this.#hidden.return(content);
    if (this.#connected) this.#reflect(this.#state === "ready");
  }

  /** Swaps to the real content. Honors `minDuration` to prevent a flash. */
  ready(): void {
    // The first signal wins: a repeat while the floor still holds the reveal back
    // must not restart the wait, or a stream of ready events keeps postponing it.
    if (this.#state === "ready" || this.#floor.pending) return;
    this.#floor.schedule(this.#safeMinDuration, () => this.#reveal());
  }

  /** Returns to the loading state (e.g. a Turbo Stream re-fetch). */
  reset(): void {
    this.#floor.cancel();
    this.#enterLoading();
  }

  /** Shows the placeholder, hides content, and marks the region busy. */
  #enterLoading(): void {
    this.#floor.begin();
    this.#reflect(false);
    this.element.setAttribute("aria-busy", "true");
    this.element.setAttribute("data-state", "loading");
  }

  /**
   * Hides the placeholder, shows content, and clears the busy state.
   *
   * @stimeoRuntimeOnly `announceReadyText` is the wording of the one announcement this reveal
   *   makes.
   */
  #reveal(): void {
    this.#reflect(true);
    this.element.setAttribute("aria-busy", "false");
    this.element.setAttribute("data-state", "ready");
    this.dispatch("ready", { detail: {} });
    // loading → ready is the transition; the skeleton itself carries no words.
    announce(fillTemplate(this.announceReadyTextValue, {}));
  }

  /** Shows the placeholder or the content for the phase, on the first of each. */
  #reflect(ready: boolean): void {
    if (this.hasPlaceholderTarget) this.#hidden.write(this.placeholderTarget, ready ? "" : null);
    if (this.hasContentTarget) this.#hidden.write(this.contentTarget, ready ? null : "");
  }

  /**
   * Drops the held reveal on a real detach. The host's `aria-busy` and `data-state`
   * stay as last written, and so does the `hidden` of a placeholder or content that
   * leaves the document with it. A placeholder or content that stops resolving while
   * it stays on the page (its mark removed, or the identifier dropped from
   * `data-controller`) gets its own `hidden` back from its target callback.
   */
  #teardown(): void {
    this.#floor.cancel();
  }

  /** Current lifecycle phase as reflected on `data-state`. */
  get #state(): string {
    return this.element.getAttribute("data-state") ?? "loading";
  }
  /** Current `minDuration` declaration resolved against its numeric contract. */
  get #safeMinDuration(): number {
    return this.#numbers.read(
      this,
      "minDuration",
      this.minDurationValue,
      SkeletonController.values.minDuration.default,
      SkeletonController.valueConstraints.minDuration,
    );
  }
}
